import { useTranslation } from 'react-i18next';
import type { ReportSection as ReportSectionType } from '@reports/domain/value-objects';
import { isTableBlock, isChartBlock } from '@reports/domain/value-objects/blocks';
import { ReportTableBlock } from '@reports/presentation/components/base/ReportTableBlock';
import { ReportChartBlock } from '@reports/presentation/components/base/ReportChartBlock';
import type { BlockDataRequestDto } from '@reports/application/dtos';

interface ReportSectionProps {
  section: ReportSectionType;
  request: BlockDataRequestDto;
}

/**
 * ReportSection - Section content component for reports
 *
 * This component serves as a container for section-specific content like
 * charts and tables. It dynamically renders block components
 * based on the block type (TABLE, CHART).
 * Gets data fetching capabilities from composition root.
 *
 * @param {ReportSectionProps} props - Component properties
 * @param {ReportSection} props.section - Section data with key, title, and blocks
 * @param {BlockDataRequestDto} props.request - Request parameters (filters, date range)
 * @returns {React.ReactElement} Section content component
 *
 * @example
 * <ReportSection
 *   section={section}
 *   request={{ startDate: new Date(), endDate: new Date(), filters: {} }}
 * />
 */
export const ReportSection = ({ section, request }: ReportSectionProps) => {
  const { t } = useTranslation('reports');

  // Render blocks based on their type
  const renderBlock = (block: ReportSectionType['blocks'][number]) => {
    if (isTableBlock(block)) {
      return <ReportTableBlock blockConfig={block} key={block.key} request={request} />;
    }

    if (isChartBlock(block)) {
      return <ReportChartBlock blockConfig={block} key={block.key} request={request} />;
    }

    return null;
  };

  return (
    <div className="w-full space-y-6">
      {section.blocks.length === 0 ? (
        <div className="w-full h-[300px] rounded-lg border border-(--base-border-border) bg-white flex items-center justify-center">
          <p className="text-base text-(--base-muted-foreground)">
            {t('reportCommon:messages.noBlocksAvailable', 'No content available for this section.')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
          {section.blocks.map((block) => renderBlock(block))}
        </div>
      )}
    </div>
  );
};
